import React, { useEffect, useRef, useState } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useProductChoiceGroup } from './useProductChoiceGroup';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { Product } from '@apps/card/components/ProductUIConfig/product.types';
import { useCustomizationContext } from '../../useCardCustomization';
import { getDimensions } from '@apps/card/utils';
import { formatProductSizeParts, useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import type { ProductPickerRow } from '@apps/card/components/ProductPicker/ProductPicker.types';
import { ProductPicker, ProductPickerPrice } from '@apps/card/components/ProductPicker/ProductPicker';
import { getProductIconKind } from '@apps/card/components/ProductPicker/ProductIcon';
import { useCardCustomizerTelemetry } from '../../CardCustomizer.telemetry';
import { StartBasicDigitalConfirm } from './StartBasicDigitalConfirm';

const isProduct = (input: unknown): input is StationeryProduct & Product => !!input;

type ProductChoiceGroupProps = Readonly<{
  draftId: string;
  /** Dims the group while the caller has work of its own in flight, as a switch does. */
  busy?: boolean;
  /**
   * Stands between a pick and the switch, for a caller that must ask first. Call `select` to go
   * ahead; never calling it drops the pick. Without it, a pick switches straight away.
   */
  confirmSelect?: (productId: string, select: () => void) => void;
  /**
   * Keep the active product on screen as one unpickable row when there is nothing to switch to
   * (a single-product template, an order-mode edit). The paper editor passes it: that row is
   * where it states the size ("Napkins  Cocktail"), now that its separate Size row is gone.
   * Digital rows carry no size, so the digital editor leaves it off and renders nothing, as before.
   */
  showReadOnly?: boolean;
}>;

type ProductChoiceGroupContentProps = Readonly<{
  isSwitching: boolean;
  /** The draft has an order in the cart, so the backend will refuse any switch. */
  isLocked: boolean;
  /**
   * Nothing to switch to -- a single-product template (napkins, signs) or an order-mode edit. The
   * active product still shows as its one row: the row carries the size ("Napkins  Cocktail"), and
   * it is the only place in the editor the size is read from.
   */
  isReadOnly: boolean;
  selectProduct: (productId: string) => void;
  choiceProducts: StationeryProduct[];
}>;

const ProductChoiceGroupContent = ({ isSwitching, isLocked, isReadOnly, selectProduct, choiceProducts }: ProductChoiceGroupContentProps) => {
  const { tq } = useTranslation('stationery');
  const { activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const { getProductWithUIMapping } = useProductToUIMapping();
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  // Every switch target lives on the draft's own template, so the row titles
  // take the draft's category and size: the same "Postcard 4×6" / "Magnet 4×6"
  // / "Classic Paper 5×7" the PDP picker shows, rather than the bare product
  // name (which would call a postcard "Classic Paper").
  const [templateCategory] = useCustomizationContext(d => d.stationeryTemplateCategory, true);
  const [card] = useCustomizationContext(d => d.config.card, true);
  const { width: widthInches, height: heightInches } = getDimensions(card);
  const sizeLabels = useCardSizeLabels();

  // Read-only shows the active product alone: order mode lists the template's other products in
  // `choiceProducts`, but none can be picked, so offering them would only invite a click that
  // cannot land.
  const shownProducts = isReadOnly ? choiceProducts.filter(p => p.id === activeProduct.id) : choiceProducts;
  const products = shownProducts.map(p => getProductWithUIMapping(p)).filter(isProduct);
  const sizedOn = { category: templateCategory, widthInches, heightInches };

  // Undiscounted: the editor has no sale context, and a promo entered at
  // checkout applies there. The PDP is where the sale price is advertised.
  const rows: ProductPickerRow[] = products.map(product => {
    const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, undefined, true);
    const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
    const title = formatProductSizeParts(templateCategory, widthInches, heightInches, sizeLabels, product);
    return {
      key: product.id,
      productType: product.productType,
      icon: getProductIconKind(product, sizedOn),
      name: title.name,
      size: title.size,
      price: (
        <ProductPickerPrice
          price={unitPriceString}
          isFree={priceData.unitPriceInMinorUnits === 0}
        />
      ),
      // `isSwitching` dims the group and blocks the pointer, but a button stays in the tab order
      // and Enter still fires it -- and a keyboard select landing mid-undo would start a second
      // product mutation racing the one in flight, since the two paths hold separate locks.
      disabled: isSwitching || isLocked || isReadOnly,
    };
  });

  return (
    <Box>
      <Box
        opacity={isSwitching || isLocked ? 0.6 : 1}
        pointerEvents={isSwitching ? 'none' : 'auto'}
        transition="opacity 0.15s ease"
      >
        <ProductPicker
          rows={rows}
          value={activeProduct.id}
          onSelect={selectProduct}
          compact
          // Every other format is disabled for a cart item, so opening the list offers nothing to
          // pick, and the link would compete with the hint below. Read-only has one row to begin with.
          expandable={!isLocked && !isReadOnly}
        />
      </Box>
      {isLocked && (
        <TextV2
          typographyVariant="label2"
          color="mono12"
          marginTop={3}
        >
          {tq`Remove this item from your cart to change its format.`}
        </TextV2>
      )}
    </Box>
  );
};

/**
 * The customizer's product switcher: the same `ProductPicker` the PDP shows,
 * fed from the draft's pricing context and wired to the draft-product mutation.
 *
 * Picking Basic Digital always asks first, in either editor: it leaves this draft for a new one
 * rather than switching it, so the user is told where their card went.
 *
 * With nothing to switch to it renders nothing -- unless `showReadOnly`, when it still shows the
 * active product as one unpickable row, because that row is where the paper editor states the
 * size ("Napkins  Cocktail", "Magnet  4×6"); the separate read-only Size row it used to duplicate
 * is gone.
 */
export const ProductChoiceGroup = ({ draftId, busy, confirmSelect, showReadOnly = false }: ProductChoiceGroupProps) => {
  const { selectProduct, isSwitching, isStartingBasicDigital, isBasicDigitalReady, canSelectProduct, isLockedToCartItem, choiceProducts } = useProductChoiceGroup({
    draftId,
  });
  const { activeProduct } = useStationeryProductPricingContext();
  const { buttonInteracted } = useCardCustomizerTelemetry();
  const [pendingBasicDigitalId, setPendingBasicDigitalId] = useState<string | undefined>(undefined);
  // A paper draft starting Basic Digital leaves for the digital route, unmounting this switcher
  // before the start settles.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!canSelectProduct && !showReadOnly) return null;

  const onSelect = (productId: string) => {
    // The picker reports a click on the selected row too. Every other format's switch ignores it
    // further down, but the Basic Digital check below would take it as a request for a second,
    // fresh Basic Digital card.
    if (productId === activeProduct.id) return;
    const isBasicDigitalTarget = choiceProducts.some(p => p.id === productId && p.productType === 'basicDigitalV2');
    if (isBasicDigitalTarget) {
      buttonInteracted('startBasicDigitalConfirm_shown', { targetProductId: productId });
      setPendingBasicDigitalId(productId);
      return;
    }
    if (confirmSelect) {
      confirmSelect(productId, () => selectProduct(productId));
    } else {
      selectProduct(productId);
    }
  };

  return (
    <>
      <ProductChoiceGroupContent
        isSwitching={isSwitching || !!busy}
        isLocked={isLockedToCartItem}
        isReadOnly={!canSelectProduct}
        selectProduct={onSelect}
        choiceProducts={choiceProducts}
      />
      <StartBasicDigitalConfirm
        isOpen={!!pendingBasicDigitalId}
        isStarting={isStartingBasicDigital}
        isPreparing={!isBasicDigitalReady}
        // Held open until the start settles: on success the page has moved to the new draft, and on
        // failure `selectProduct` has already toasted.
        onConfirm={async () => {
          if (pendingBasicDigitalId) await selectProduct(pendingBasicDigitalId);
          if (isMountedRef.current) setPendingBasicDigitalId(undefined);
        }}
        onCancel={source => {
          buttonInteracted('startBasicDigitalConfirm_keepEditingClicked', { source });
          setPendingBasicDigitalId(undefined);
        }}
      />
    </>
  );
};
